import { createOpenAIClient } from '~/utils/openaiClient';
import { reportError } from '~/utils/reportError';

const ROOM_NAME_MATCH_MODEL = 'gpt-4.1-mini';

type RoomNameMatch = { salesforceName: string; matchedRoomTypeName: string | null };

export interface RoomNameMatchesResult {
  /** True when the AI call itself failed, so an empty `matches` means "unknown", not "nothing matched". */
  hasFailed: boolean;
  /** Salesforce room name -> the exact existing room type name it was matched to. */
  matches: Record<string, string>;
}

const ROOM_NAME_MATCH_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    matches: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          salesforceName: { type: 'string' },
          matchedRoomTypeName: { type: ['string', 'null'] },
        },
        required: ['salesforceName', 'matchedRoomTypeName'],
        additionalProperties: false,
      },
    },
  },
  required: ['matches'],
  additionalProperties: false,
};

function parseRoomNameMatchResponse(content: string): Array<RoomNameMatch> | null {
  try {
    const parsed = JSON.parse(content);
    const matches = parsed?.matches;

    if (!Array.isArray(matches)) {
      return null;
    }

    return matches.filter(
      (entry): entry is RoomNameMatch =>
        !!entry &&
        typeof entry.salesforceName === 'string' &&
        (entry.matchedRoomTypeName === null || typeof entry.matchedRoomTypeName === 'string'),
    );
  } catch {
    return null;
  }
}

// The only room-name matcher in the wizard: one batched call, only trusts matches against the
// real candidate list. A failed call is reported (not swallowed) so callers don't mistake an
// outage for "nothing matched".
export async function resolveRoomNameMatchesWithAi(
  salesforceNames: Array<string | null | undefined>,
  candidateRoomTypeNames: Array<string | null | undefined>,
): Promise<RoomNameMatchesResult> {
  const uniqueSalesforceNames = Array.from(new Set(salesforceNames.filter((name): name is string => !!name?.trim())));
  const uniqueCandidateNames = Array.from(
    new Set(candidateRoomTypeNames.filter((name): name is string => !!name?.trim())),
  );

  if (uniqueSalesforceNames.length === 0 || uniqueCandidateNames.length === 0) {
    return { hasFailed: false, matches: {} };
  }

  try {
    const client = createOpenAIClient();
    const response = await client.responses.create({
      model: ROOM_NAME_MATCH_MODEL,
      input: [
        {
          role: 'system',
          content:
            'Match hotel room names between two lists that describe the same rooms with different wording, word order, or extra descriptors. Only match when confident they refer to the same room type. Room names often overlap, e.g. "One King Standard" and "One King Standard Harbour View" are different rooms: only pick the longer, more specific name when the list A name itself contains that extra detail, otherwise prefer the exact shorter name, and use null if neither is clearly the same room.',
        },
        {
          role: 'user',
          content: [
            'List A room names need to be matched to list B room names.',
            'Names may differ in word order, phrasing, or punctuation (e.g. "1 King, Deluxe Pool View, Non Smoking" and "Deluxe King Pool View" describe the same room).',
            'Return one entry per list A name. Use "matchedRoomTypeName": null when no list B entry confidently matches. Otherwise use the exact string from list B.',
            `List A (Salesforce room names): ${JSON.stringify(uniqueSalesforceNames)}`,
            `List B (existing room type names): ${JSON.stringify(uniqueCandidateNames)}`,
          ].join('\n'),
        },
      ],
      text: {
        format: {
          type: 'json_schema',
          name: 'room_name_matches',
          schema: ROOM_NAME_MATCH_RESPONSE_SCHEMA,
          strict: true,
        },
      },
    });

    const parsedMatches = parseRoomNameMatchResponse(response.output_text || '');

    if (!parsedMatches) {
      reportError(new Error('Room name matcher returned an unparseable response'));
      return { hasFailed: true, matches: {} };
    }

    const candidateNameSet = new Set(uniqueCandidateNames);
    const matches = parsedMatches.reduce<Record<string, string>>(
      (resolved, { salesforceName, matchedRoomTypeName }) => {
        if (matchedRoomTypeName && candidateNameSet.has(matchedRoomTypeName)) {
          resolved[salesforceName] = matchedRoomTypeName;
        }

        return resolved;
      },
      {},
    );

    return { hasFailed: false, matches };
  } catch (error) {
    reportError(error);
    return { hasFailed: true, matches: {} };
  }
}
